import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Shield,
  Radio,
  Library,
  Users,
  FlaskConical,
  User,
  LogOut,
  Bookmark,
  Briefcase,
  BarChart3,
  Brain,
  MessageCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

interface HeaderProps {
  isAdmin: boolean
  isApproved?: boolean
  hasPhone?: boolean
  activePage?: 'feed' | 'portfolio'
  trackedCount?: number
  onBroadcastClick: () => void
  onDocumentsClick: () => void
  onUserManagementClick: () => void
  onSandboxClick: () => void
  onPortfolioClick: () => void
  onTrackedClick: () => void
  onConversationHistoryClick: () => void
  onProfileClick: () => void
  onLogoutClick: () => void
}

export function Header({
  isAdmin,
  isApproved = true,
  hasPhone = true,
  activePage = 'feed',
  trackedCount = 0,
  onBroadcastClick,
  onDocumentsClick,
  onUserManagementClick,
  onSandboxClick,
  onPortfolioClick,
  onTrackedClick,
  onConversationHistoryClick,
  onProfileClick,
  onLogoutClick,
}: HeaderProps) {
  const { t } = useTranslation()
  const [adminMenuOpen, setAdminMenuOpen] = useState(false)
  const [brainMenuOpen, setBrainMenuOpen] = useState(false)
  const adminRef = useRef<HTMLDivElement>(null)
  const brainRef = useRef<HTMLDivElement>(null)
  const handlerRef = useRef<(e: MouseEvent) => void>()

  useEffect(() => {
    if (handlerRef.current) {
      document.removeEventListener('mousedown', handlerRef.current)
    }

    if (!adminMenuOpen && !brainMenuOpen) {
      handlerRef.current = undefined
      return
    }

    const handler = (e: MouseEvent) => {
      if (adminRef.current && !adminRef.current.contains(e.target as Node)) {
        setAdminMenuOpen(false)
      }
      if (brainRef.current && !brainRef.current.contains(e.target as Node)) {
        setBrainMenuOpen(false)
      }
    }
    handlerRef.current = handler
    document.addEventListener('mousedown', handler)

    return () => document.removeEventListener('mousedown', handler)
  }, [adminMenuOpen, brainMenuOpen])

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/80 border-b border-white/[0.06]">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <span
              className="text-black text-2xl"
              style={{
                fontFamily: "'garamond-atf-text', 'EB Garamond', serif",
                fontStyle: 'italic',
                WebkitTextStroke: '1px black',
              }}
            >
              S
            </span>
          </div>
          <span
            className="text-white font-semibold tracking-tight"
            style={{
              fontFamily: "'garamond-atf-text', 'EB Garamond', serif",
              fontStyle: 'normal',
              fontSize: '1.25rem',
            }}
          >
            Sovereign
          </span>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-1">
          {/* Admin Dropdown */}
          {isAdmin && (
            <div className="relative" ref={adminRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setAdminMenuOpen((prev) => !prev)
                  setBrainMenuOpen(false)
                }}
                className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                title="Admin Panel"
              >
                <Shield className="w-5 h-5" />
              </Button>

              {adminMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-neutral-950/90 border border-white/[0.08] rounded-xl shadow-2xl shadow-black/50 backdrop-blur-xl overflow-hidden z-50">
                  <div className="px-3 py-2 border-b border-white/[0.06]">
                    <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">
                      Admin Panel
                    </p>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setAdminMenuOpen(false)
                        onBroadcastClick()
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                    >
                      <Radio className="w-4 h-4 text-emerald-400" />
                      Broadcast
                    </button>
                    <button
                      onClick={() => {
                        setAdminMenuOpen(false)
                        onDocumentsClick()
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                    >
                      <Library className="w-4 h-4 text-cyan-400" />
                      Documents
                    </button>
                    <button
                      onClick={() => {
                        setAdminMenuOpen(false)
                        onUserManagementClick()
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                    >
                      <Users className="w-4 h-4 text-amber-400" />
                      User Management
                    </button>
                    <button
                      onClick={() => {
                        setAdminMenuOpen(false)
                        onSandboxClick()
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                    >
                      <FlaskConical className="w-4 h-4 text-pink-400" />
                      AI Sandbox
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Page toggle: Feed <-> Portfolio */}
          {isApproved && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onPortfolioClick}
              className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors"
              title={
                activePage === 'feed' ? t('portfolioTitle') : t('tradingFeed')
              }
            >
              {activePage === 'feed' ? (
                <Briefcase className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
          )}

          {/* Brain Dropdown: Tracked Insights + Conversation History */}
          {isApproved && (
            <div className="relative" ref={brainRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setBrainMenuOpen((prev) => !prev)
                  setAdminMenuOpen(false)
                }}
                className="text-pink-400 hover:text-pink-300 hover:bg-pink-500/10 transition-colors relative"
                title={t('insightsAndConversations')}
              >
                <Brain className="w-5 h-5" />
                {trackedCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-pink-500 text-black text-[10px] font-bold rounded-full flex items-center justify-center">
                    {trackedCount > 9 ? '9+' : trackedCount}
                  </span>
                )}
              </Button>

              {brainMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-neutral-950/90 border border-white/[0.08] rounded-xl shadow-2xl shadow-black/50 backdrop-blur-xl overflow-hidden z-50">
                  <div className="px-3 py-2 border-b border-white/[0.06]">
                    <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">
                      {t('yourActivity')}
                    </p>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setBrainMenuOpen(false)
                        onTrackedClick()
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                    >
                      <Bookmark className="w-4 h-4 text-amber-400" />
                      {t('trackedInsights')}
                      {trackedCount > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded text-[10px] font-semibold">
                          {trackedCount}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setBrainMenuOpen(false)
                        onConversationHistoryClick()
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                    >
                      <MessageCircle className="w-4 h-4 text-pink-400" />
                      {t('aiConversations')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Profile Icon */}
          {isApproved && (
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={onProfileClick}
                className="text-neutral-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                title={t('viewProfile')}
              >
                <User className="w-5 h-5" />
              </Button>
              {!hasPhone && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-amber-500 rounded-full ring-2 ring-neutral-950 animate-pulse" />
              )}
            </div>
          )}

          {/* Logout */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onLogoutClick}
            className="text-neutral-400 hover:text-white hover:bg-white/[0.06] transition-colors"
            title={t('logout')}
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </header>
  )
}
